import type { APILibraryCategoryResponseType } from "@/core/data_sources/models/APILibraryCategory";
import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import type { APILibraryTemplatePackResponseType } from "@/core/data_sources/models/APILibraryTemplatePack";
import type { APILibraryVerticalResponseType } from "@/core/data_sources/models/APILibraryVertical";
import {
  AppLink,
  Badge,
  KebabButton,
  TypeIcon,
  type BadgeTone,
} from "@/core/ui/atoms";
import { TagList, type DataTableColumn } from "@/core/ui/molecules";

type FormTemplateItem = {
  readonly categories: readonly string[];
  readonly description: string;
  readonly favorite: boolean;
  readonly fields: number;
  readonly id: string;
  readonly lastUpdated: string;
  readonly name: string;
  readonly recommended: boolean;
  readonly sections: number;
  readonly source: "custom" | "library";
  readonly status: "active" | "disabled" | "draft";
  readonly type: "form" | "media" | "model";
  readonly usedInForms: number;
  readonly usedInJobs: number;
  readonly verticals: readonly string[];
  readonly version: string;
};

type FormTemplatePack = {
  readonly activatedAt?: string;
  readonly activatedBy?: string;
  readonly dependencyIds: readonly string[];
  readonly description: string;
  readonly id: string;
  readonly lastUpdated: string;
  readonly name: string;
  readonly recommended: boolean;
  readonly status: "active" | "disabled";
  readonly templateIds: readonly (string | number)[];
  readonly verticals: readonly string[];
};

import { TemplateBreakdown } from "./TemplateBreakdown";
import {
  countTemplatesByCategory,
  countTemplatesByVertical,
  getPackPath,
} from "../../utils";

import type { CatalogMode } from "../../utils";

const statusTone = {
  active: "success",
  disabled: "disabled",
  draft: "pending",
} as const;

const statusLabel = {
  active: "Active",
  disabled: "Disabled",
  draft: "Draft",
} as const;

const libraryTemplateStatusTone = {
  archived: "disabled",
  draft: "pending",
  published: "success",
} as const;

const libraryTemplateStatusLabel = {
  archived: "Archived",
  draft: "Draft",
  published: "Published",
} as const;

export const templateColumns: readonly DataTableColumn<FormTemplateItem>[] = [
  {
    header: "Template",
    key: "template",
    render: (template) => (
      <div className="flex items-center gap-2">
        <TypeIcon type={template.type} />
        <div>
          <div className="font-medium text-ink">
            {template.name}
            {template.recommended ? (
              <Badge
                className="ml-1.5 px-1.5 py-0 text-[10px]"
                tone="recommended"
              >
                Star
              </Badge>
            ) : null}
          </div>
          <div className="text-[11px] text-ink-subtle">
            v{template.version} - {template.lastUpdated}
          </div>
        </div>
      </div>
    ),
  },
  {
    header: "Type",
    key: "type",
    render: (template) => (
      <span className="capitalize text-ink-muted">{template.type}</span>
    ),
  },
  {
    header: "Vertical",
    key: "vertical",
    render: (template) => <TagList values={template.verticals} />,
  },
  {
    header: "Source",
    key: "source",
    render: (template) =>
      template.source === "library" ? (
        <Badge dot tone="global">
          Library
        </Badge>
      ) : (
        <Badge tone="neutral">Custom</Badge>
      ),
  },
  {
    header: "Status",
    key: "status",
    render: (template) => (
      <Badge dot tone={statusTone[template.status]}>
        {statusLabel[template.status]}
      </Badge>
    ),
  },
  {
    header: "Used in",
    key: "usedIn",
    render: (template) => (
      <span className="text-xs text-info">
        {template.usedInForms > 0
          ? `${template.usedInForms} forms`
          : `${template.usedInJobs} jobs`}
      </span>
    ),
  },
  {
    className: "w-10",
    header: "",
    key: "menu",
    render: () => <KebabButton />,
  },
];

export const libraryTemplateColumns: readonly DataTableColumn<APILibraryTemplateResponseType>[] =
  createLibraryTemplateColumns();

export function createLibraryTemplateColumns(
  onView?: (template: APILibraryTemplateResponseType) => void,
): readonly DataTableColumn<APILibraryTemplateResponseType>[] {
  return [
    {
      header: "Template",
      key: "template",
      render: (template) => (
        <button
          className="flex items-center gap-2 text-left"
          onClick={() => onView?.(template)}
          type="button"
        >
          {template.type ? <TypeIcon type={template.type} /> : null}
          <div>
            <div className="font-medium text-ink">
              {template.name}
              {template.recommended ? (
                <Badge
                  className="ml-1.5 px-1.5 py-0 text-[10px]"
                  tone="recommended"
                >
                  Star
                </Badge>
              ) : null}
            </div>
            <div className="text-[11px] text-ink-subtle">
              v{template.version ?? "-"} - {formatLibraryTemplateDate(template)}
            </div>
          </div>
        </button>
      ),
    },
    {
      header: "Type",
      key: "type",
      render: (template) => (
        <span className="capitalize text-ink-muted">
          {template.type ?? "Unknown"}
        </span>
      ),
    },
    {
      header: "Vertical",
      key: "vertical",
      render: (template) => (
        <TagList values={getLibraryTemplateVerticalNames(template)} />
      ),
    },
    {
      header: "Source",
      key: "source",
      render: (template) =>
        template.source === "library" ? (
          <Badge dot tone="global">
            Library
          </Badge>
        ) : (
          <Badge tone="neutral">{template.source ?? "Unknown"}</Badge>
        ),
    },
    {
      header: "Status",
      key: "status",
      render: (template) => (
        <Badge
          dot
          tone={
            template.status
              ? libraryTemplateStatusTone[template.status]
              : "disabled"
          }
        >
          {template.status
            ? libraryTemplateStatusLabel[template.status]
            : "Unknown"}
        </Badge>
      ),
    },
    {
      header: "Content",
      key: "content",
      render: (template) => (
        <span className="text-xs text-info">
          {getLibraryTemplateContentLabel(template)}
        </span>
      ),
    },
    {
      className: "w-10",
      header: "",
      key: "menu",
      render: (template) => <KebabButton onClick={() => onView?.(template)} />,
    },
  ];
}

export const verticalColumns: readonly DataTableColumn<APILibraryVerticalResponseType>[] =
  createVerticalColumns();

export function createVerticalColumns(
  onEdit?: (vertical: APILibraryVerticalResponseType) => void,
): readonly DataTableColumn<APILibraryVerticalResponseType>[] {
  return [
    {
      header: "Code",
      key: "code",
      render: (vertical) => (
        <span className="font-mono text-xs text-ink-muted">
          {vertical.code}
        </span>
      ),
    },
    {
      header: "Name",
      key: "name",
      render: (vertical) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{vertical.name}</span>
          {vertical.is_system ? (
            <Badge className="text-[10px]" tone="neutral">
              System
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      header: "Status",
      key: "status",
      render: (vertical) => (
        <Badge dot tone={vertical.status === "active" ? "success" : "disabled"}>
          {vertical.status === "active" ? "Active" : "Disabled"}
        </Badge>
      ),
    },
    {
      header: "Used by",
      key: "usedBy",
      render: (vertical) => (
        <span className="text-xs text-info">
          {countTemplatesByVertical(vertical.name ?? "")} templates
        </span>
      ),
    },
    {
      className: "w-10",
      header: "",
      key: "menu",
      render: (vertical) =>
        vertical.is_system ? (
          <span className="inline-flex h-7 w-7 items-center justify-center text-ink-subtle opacity-30">
            -
          </span>
        ) : (
          <KebabButton onClick={() => onEdit?.(vertical)} />
        ),
    },
  ];
}

export const categoryColumns: readonly DataTableColumn<APILibraryCategoryResponseType>[] =
  createCategoryColumns();

export function createCategoryColumns(
  onEdit?: (category: APILibraryCategoryResponseType) => void,
): readonly DataTableColumn<APILibraryCategoryResponseType>[] {
  return [
    {
      header: "Name",
      key: "name",
      render: (category) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{category.name}</span>
          {category.is_system ? (
            <Badge className="text-[10px]" tone="neutral">
              System
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      header: "Used by",
      key: "usedBy",
      render: (category) => (
        <span className="text-xs text-info">
          {countTemplatesByCategory(category.name ?? "")} templates
        </span>
      ),
    },
    {
      className: "w-10",
      header: "",
      key: "menu",
      render: (category) =>
        category.is_system ? (
          <span className="inline-flex h-7 w-7 items-center justify-center text-ink-subtle opacity-30">
            -
          </span>
        ) : (
          <KebabButton onClick={() => onEdit?.(category)} />
        ),
    },
  ];
}

export const packColumnsByMode = {
  "super-admin": createPackColumns("super-admin"),
} as const satisfies Record<
  CatalogMode,
  readonly DataTableColumn<FormTemplatePack>[]
>;

export const libraryPackColumnsByMode = {
  "super-admin": createLibraryPackColumns("super-admin"),
} as const satisfies Record<
  CatalogMode,
  readonly DataTableColumn<APILibraryTemplatePackResponseType>[]
>;

function createPackColumns(
  mode: CatalogMode,
): readonly DataTableColumn<FormTemplatePack>[] {
  return [
    {
      header: "Pack",
      key: "pack",
      render: (pack) => (
        <div>
          <div className="font-medium text-ink">
            <AppLink to={getPackPath(mode, pack.id)}>{pack.name}</AppLink>
            {pack.recommended ? (
              <Badge
                className="ml-1.5 px-1.5 py-0 text-[10px]"
                tone="recommended"
              >
                Star
              </Badge>
            ) : null}
          </div>
          <div className="max-w-xl text-[11px] text-ink-subtle">
            {pack.description}
          </div>
        </div>
      ),
    },
    {
      header: "Vertical",
      key: "vertical",
      render: (pack) => <TagList values={pack.verticals} />,
    },
    {
      header: "Templates",
      key: "templates",
      render: (pack) => <TemplateBreakdown pack={pack} />,
    },
    {
      header: "Status",
      key: "status",
      render: (pack) => (
        <Badge dot tone={pack.status === "active" ? "success" : "disabled"}>
          {pack.status === "active" ? "Active" : "Disabled"}
        </Badge>
      ),
    },
    {
      header: "Updated",
      key: "updated",
      render: (pack) => <span>{pack.lastUpdated.replace(", 2026", "")}</span>,
    },
    {
      className: "w-10",
      header: "",
      key: "menu",
      render: () => <KebabButton />,
    },
  ];
}

function createLibraryPackColumns(
  mode: CatalogMode,
): readonly DataTableColumn<APILibraryTemplatePackResponseType>[] {
  return [
    {
      header: "Pack",
      key: "pack",
      render: (pack) => (
        <div>
          <div className="font-medium text-ink">
            <AppLink to={getPackPath(mode, String(pack.id ?? ""))}>
              {pack.name}
            </AppLink>
          </div>
          <div className="max-w-xl text-[11px] text-ink-subtle">
            {pack.description}
          </div>
        </div>
      ),
    },
    {
      header: "Vertical",
      key: "vertical",
      render: (pack) => <TagList values={getLibraryPackVerticalNames(pack)} />,
    },
    {
      header: "Templates",
      key: "templates",
      render: (pack) => getLibraryPackTemplateBreakdown(pack),
    },
    {
      header: "Status",
      key: "status",
      render: (pack) => (
        <Badge dot tone={getLibraryPackStatusTone(pack.status)}>
          {getLibraryPackStatusLabel(pack.status)}
        </Badge>
      ),
    },
    {
      header: "Updated",
      key: "updated",
      render: (pack) => <span>{formatLibraryPackDate(pack)}</span>,
    },
    {
      className: "w-10",
      header: "",
      key: "menu",
      render: () => <KebabButton />,
    },
  ];
}

function formatLibraryTemplateDate(template: APILibraryTemplateResponseType) {
  return (
    template.modified?.split("T")[0] ?? template.created?.split("T")[0] ?? "-"
  );
}

function getLibraryTemplateVerticalNames(
  template: APILibraryTemplateResponseType,
) {
  return (
    template.verticals
      ?.map((vertical) => vertical.name ?? vertical.code ?? "")
      .filter(Boolean) ?? []
  );
}

function getLibraryTemplateContentLabel(
  template: APILibraryTemplateResponseType,
) {
  if (typeof template.fields_count === "number" && template.fields_count > 0) {
    return `${template.fields_count} fields`;
  }
  if (typeof template.shots_count === "number" && template.shots_count > 0) {
    return `${template.shots_count} shots`;
  }
  return "-";
}

function formatLibraryPackDate(pack: APILibraryTemplatePackResponseType) {
  return pack.modified?.split("T")[0] ?? pack.created?.split("T")[0] ?? "-";
}

function getLibraryPackVerticalNames(pack: APILibraryTemplatePackResponseType) {
  return (
    pack.verticals
      ?.map((vertical) => vertical.name ?? vertical.code ?? "")
      .filter(Boolean) ?? []
  );
}

function getLibraryPackTemplateBreakdown(
  pack: APILibraryTemplatePackResponseType,
) {
  const templates = pack.templates ?? [];
  const formCount = templates.filter(
    (template) => template.type === "form",
  ).length;
  const mediaCount = templates.filter(
    (template) => template.type === "media",
  ).length;
  const modelCount = templates.filter(
    (template) => template.type === "model",
  ).length;

  return (
    <span>
      <strong>{templates.length}</strong>{" "}
      <span className="text-[11px] text-ink-subtle">
        ({formCount}F - {mediaCount}M - {modelCount}D)
      </span>
    </span>
  );
}

function getLibraryPackStatusTone(status?: string): BadgeTone {
  if (status === "active") {
    return "success";
  }
  return "disabled";
}

function getLibraryPackStatusLabel(status?: string) {
  if (!status) {
    return "Unknown";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}
